import type { CSSProperties } from "react";
import { useState } from "react";
import type { AnalyticsDateRange } from "../../hooks/useAnalyticsDashboard";
import {
  correctAnalyticsDateRange,
  isIsoDate,
  isValidAnalyticsDateRange,
} from "../../utils/analyticsDateRange.utils";

type AnalyticsDateRangeFilterProps = {
  dateRange: AnalyticsDateRange;
  onDateRangeChange: (range: AnalyticsDateRange) => void;
  disabled?: boolean;
};

const dateInputStyle: CSSProperties = {
  font: "inherit",
  fontSize: "0.875rem",
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid var(--p-color-border-secondary, #E1E3E5)",
  color: "var(--p-color-text, #202223)",
  background: "var(--p-color-bg-surface, #fff)",
  minWidth: "10.5rem",
};

export function AnalyticsDateRangeFilter({
  dateRange,
  onDateRangeChange,
  disabled = false,
}: AnalyticsDateRangeFilterProps) {
  const [draft, setDraft] = useState<AnalyticsDateRange>(dateRange);

  const applyField = (field: keyof AnalyticsDateRange, value: string) => {
    const next = { ...draft, [field]: value };
    setDraft(next);

    // While the user is still typing, keep the local draft only.
    if (!isIsoDate(value)) return;

    const corrected = correctAnalyticsDateRange(next, field);

    if (
      corrected.date_from !== next.date_from ||
      corrected.date_to !== next.date_to
    ) {
      setDraft(corrected);
    }

    if (!isValidAnalyticsDateRange(corrected)) return;
    if (
      corrected.date_from !== dateRange.date_from ||
      corrected.date_to !== dateRange.date_to
    ) {
      onDateRangeChange(corrected);
    }
  };

  return (
    <s-box
      background="base"
      border="small subdued solid"
      borderRadius="large-100"
      padding="large-200"
    >
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-icon type="calendar" />
          <s-text type="strong">Periodo</s-text>
          <s-text color="subdued">(UTC)</s-text>
        </s-stack>

        <s-stack direction="inline" gap="base" alignItems="center">
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span
              style={{
                fontSize: "0.75rem",
                color: "var(--p-color-text-secondary, #6D7175)",
              }}
            >
              Desde
            </span>
            <input
              type="date"
              value={draft.date_from}
              disabled={disabled}
              style={dateInputStyle}
              onChange={(e) => applyField("date_from", e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span
              style={{
                fontSize: "0.75rem",
                color: "var(--p-color-text-secondary, #6D7175)",
              }}
            >
              Hasta
            </span>
            <input
              type="date"
              value={draft.date_to}
              disabled={disabled}
              style={dateInputStyle}
              onChange={(e) => applyField("date_to", e.target.value)}
            />
          </label>
        </s-stack>
      </s-stack>
    </s-box>
  );
}
